import React, { useEffect, useState } from 'react';
import { ArrowRight, Smartphone, X, Download } from 'lucide-react';
import { clsx } from '../utils/helpers';

// Non-standard event — not in the default TS DOM lib.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const HIDE_FOREVER_KEY = 'pwa_install_prompt_hide_forever';

type Platform = 'ios' | 'android';

const IOS_STEPS = [
  { title: 'Tap the menu icon', desc: 'Tap the three lines beside the address bar at the bottom of the page.' },
  { title: 'Tap Share', desc: 'Tap the Share icon in the menu that appears.' },
  { title: 'Tap "View More"', desc: 'In the share sheet, tap "View More" to see all options.' },
  { title: 'Add to Home Screen', desc: 'Tap "Add to Home Screen" and confirm to finish.' },
];

const ANDROID_STEPS = [
  { title: 'Open the browser menu', desc: 'Tap the three-dot menu icon in the top right corner of Chrome.' },
  { title: 'Tap "Install app"', desc: 'Look for "Install app" or "Add to Home Screen" in the menu.' },
  { title: 'Confirm "Install"', desc: 'Confirm the install prompt and the app icon will appear on your home screen.' },
];

const detectPlatform = (): Platform => (/iPad|iPhone|iPod/.test(window.navigator.userAgent) ? 'ios' : 'android');

const isMobileDevice = () => /iPad|iPhone|iPod|Android|Mobi/.test(window.navigator.userAgent);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

// Persistent floating button, not an auto-popup — the user opens the install
// instructions when they want them, instead of the modal showing itself
// unprompted on every login.
const InstallAppPrompt: React.FC = () => {
  const [showFab, setShowFab] = useState(false);
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState<Platform>('ios');
  const [step, setStep] = useState(1);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (isStandalone()) return; // already installed/running as an app
    if (!isMobileDevice()) return; // desktop — this flow is mobile-specific
    if (localStorage.getItem(HIDE_FOREVER_KEY) === 'true') return;
    setPlatform(detectPlatform());
    setShowFab(true);
  }, []);

  const openModal = () => { setStep(1); setOpen(true); };
  const close = () => setOpen(false);

  const dismissForever = () => {
    localStorage.setItem(HIDE_FOREVER_KEY, 'true');
    setShowFab(false);
    close();
  };

  const switchPlatform = (p: Platform) => {
    setPlatform(p);
    setStep(1);
  };

  const steps = platform === 'ios' ? IOS_STEPS : ANDROID_STEPS;
  const canNativeInstall = platform === 'android' && !!deferredPrompt;

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') { localStorage.setItem(HIDE_FOREVER_KEY, 'true'); setShowFab(false); }
    close();
  };

  const handleNextStep = () => {
    if (step < steps.length) setStep(step + 1);
    else close();
  };

  return (
    <>
      {showFab && !open && (
        <button onClick={openModal} aria-label="Install app"
          className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-xl flex items-center justify-center transition-colors">
          <Download size={22} />
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={close} />
          <div className="relative w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl animate-slide-up">
            <div className="flex justify-center pt-3 sm:hidden">
              <div className="w-10 h-1.5 rounded-full bg-gray-200" />
            </div>

            <button onClick={close} aria-label="Close" className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>

            <div className="px-6 pt-4 pb-2 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                <Smartphone size={22} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Install Our App</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add to your home screen for the best experience</p>
              </div>
            </div>

            <div className="px-6 pt-3">
              <div className="flex rounded-full bg-gray-100 p-1">
                <button onClick={() => switchPlatform('ios')}
                  className={clsx('flex-1 py-2 rounded-full text-xs font-semibold transition-all', platform === 'ios' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500')}>
                  iPhone / iPad
                </button>
                <button onClick={() => switchPlatform('android')}
                  className={clsx('flex-1 py-2 rounded-full text-xs font-semibold transition-all', platform === 'android' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500')}>
                  Android
                </button>
              </div>
            </div>

            <div className="px-6 pt-4">
              {canNativeInstall ? (
                <div className="rounded-2xl bg-green-50 border border-green-100 p-4 text-center">
                  <p className="text-sm font-semibold text-green-800">One-tap install available</p>
                  <p className="text-xs text-green-700 mt-1">Your browser can install PoultryPro directly — no manual steps needed.</p>
                </div>
              ) : (
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs font-bold text-blue-600 tracking-wide mb-3">HOW TO INSTALL</p>
                  <div className="space-y-3.5">
                    {steps.map((s, i) => {
                      const n = i + 1;
                      const active = n === step;
                      const done = n < step;
                      return (
                        <div key={s.title} className="flex gap-3">
                          <span className={clsx(
                            'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5',
                            active ? 'bg-green-700 text-white' : done ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-400'
                          )}>
                            {n}
                          </span>
                          <div>
                            <p className={clsx('text-sm font-semibold', active ? 'text-gray-900' : 'text-gray-400')}>{s.title}</p>
                            <p className={clsx('text-xs mt-0.5', active ? 'text-gray-500' : 'text-gray-300')}>{s.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 pt-5 pb-3">
              {canNativeInstall ? (
                <button onClick={handleNativeInstall}
                  className="w-full py-3.5 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors">
                  Install App
                </button>
              ) : (
                <button onClick={handleNextStep}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors">
                  {step < steps.length ? 'Next Step' : 'Got It'}
                  {step < steps.length && <ArrowRight size={16} />}
                </button>
              )}
            </div>

            <div className="px-6 pb-6 text-center">
              <button onClick={close} className="text-sm font-semibold text-gray-700 hover:text-gray-900">Maybe Later</button>
              <div>
                <button onClick={dismissForever} className="text-xs text-gray-400 hover:text-gray-600 mt-1">Don't show again</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppPrompt;
